/**
 * Tipos e utilitários compartilhados pelas três abas de "Tabela de Preços
 * Contratuais": Componentes da Remuneração, Parâmetros de Retenções e Faixas
 * Progressivas do IR.
 *
 * Valores monetários seguem a forma canônica do banco ("1234.56", ver
 * `@/nucleo/dinheiro`) — mesmo sem persistência real ainda, o formato já é o
 * que a API vai usar quando o módulo nascer. Percentuais são fração decimal
 * (0.022 = 2,2%), como no restante do sistema.
 */

export type TipoPessoa = 'PF' | 'PJ' | 'COOPERATIVA';

export const TIPO_PESSOA_LABELS: Record<TipoPessoa, string> = {
  PF: 'Pessoa Física',
  PJ: 'Pessoa Jurídica',
  COOPERATIVA: 'Cooperativa',
};

export interface ComponenteRemuneracao {
  id: string;
  nome: string;
  tipoPessoa: TipoPessoa;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  versao: number;
  perCapita: string;
  fixoMensal: string;
  cadeirante: string;
  crechePerCapita: string;
  especialFixo: string;
  kmAdicional: string;
  ativComplRegularPct: number;
  ativComplCadeirantePct: number;
  ccaPct: number;
  alunosMinFixo: number;
  /** Interruptor manual, independente da vigência — uma versão pode estar dentro do
   *  período e ainda assim desativada (ex.: cadastrada por engano). */
  ativa: boolean;
  observacoes: string;
}

export interface ParametroRetencao {
  id: string;
  nome: string;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  inssPiso: string;
  inssTeto: string;
  inssBaseCalculo: string;
  inssAliquotaEfetiva: number;
  sestSenatAliquota: number;
  cooperativaAliquota: number;
  irBasePct: number;
  irNovaBasePct: number;
  outrosAliquota: number;
  /** Interruptor manual, independente da vigência — ver o mesmo campo em `ComponenteRemuneracao`. */
  ativa: boolean;
  observacoes: string;
}

export interface FaixaIr {
  id: string;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  ordem: number;
  limiteInferior: string;
  limiteSuperior: string | null;
  aliquota: number;
  parcelaDeduzir: string;
  observacoes: string;
}

export interface DadosTabelaDePrecos {
  remuneracao: ComponenteRemuneracao[];
  retencoes: ParametroRetencao[];
  faixasIr: FaixaIr[];
}

/** "2026-01-01" -> "01/01/2026". Manipulação de string, sem passar por Date/fuso. */
export function formatarDataBr(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** 0.022 -> "2,2%". */
export function formatarPercentual(valor: number): string {
  return `${(valor * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 })}%`;
}

/** Data de hoje no formato canônico "AAAA-MM-DD", para comparar com vigências. */
export function hojeIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Uma versão está vigente quando hoje cai dentro do intervalo de vigência. */
export function estaVigente(
  vigenciaInicio: string,
  vigenciaFim: string | null,
  hoje = hojeIso(),
): boolean {
  return vigenciaInicio <= hoje && (!vigenciaFim || vigenciaFim >= hoje);
}

/** Gera um id local simples para itens criados na tela (sem persistência real). */
export function gerarIdLocal(): string {
  return `local-${Date.now()}-${Math.round(Math.random() * 100000)}`;
}
